import { useTranslation } from "react-i18next";
import type { ManualState } from "@arc/contracts";
import { cn } from "@/lib/utils";
import { MANUAL_STATE_META, parseMarkComment } from "./ManualStateBadge.js";

/** The linear release track (the `marked` flag sits OFF it). Mirrors the legacy Entwurf → In Prüfung → Freigegeben. */
const FLOW: Exclude<ManualState, "marked">[] = ["draft", "ready_for_review", "confirmed"];

/** The stepper accent = the CURRENT state's colour (dot fill + the reached connector line), from the {@link
 *  MANUAL_STATE_META} palette: **draft = NO colour** (neutral), ready-for-review = amber (`attention`), confirmed = green
 *  (`success`). Past-but-reached steps render neutral "done" (grey) — NOT their transient colour — so a confirmed manual
 *  doesn't show "Ready for review" glowing amber (which reads as *still awaiting*). Theme-aware tokens; no brand-primary. */
const ACCENT: Record<Exclude<ManualState, "marked">, { dot: string; line: string }> = {
  draft: { dot: "bg-muted text-muted-foreground", line: "bg-border" },
  ready_for_review: { dot: "bg-attention text-attention-ink", line: "bg-attention" },
  confirmed: { dot: "bg-success text-success-foreground", line: "bg-success" },
};

/**
 * A **read-only** release-status indicator (ADR-016 §D) — where the manual sits on the Draft → Ready-for-review →
 * Confirmed track. The **current** state's colour is the single accent (draft neutral, ready amber, confirmed green): it
 * fills the current step's circle and the reached connector line up to it; earlier steps are neutral grey "done", future
 * steps a **dashed** outline. Circles are opaque so the connector reads as one fully-joined line (no gap, no overshoot
 * past the last step). NOT interactive: the review *transitions* (ADR-018) are the buttons rendered BELOW this by
 * {@link ManualDetailsDrawer}. `marked` is a flag, not a step → a destructive note.
 */
export function ManualStatusStepper({ state, comment }: { state: ManualState; comment?: string | null }) {
  const { t } = useTranslation();
  if (state === "marked") {
    const { Icon } = MANUAL_STATE_META.marked;
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-destructive flex items-center gap-1.5 text-sm font-medium">
          <Icon aria-hidden="true" className="size-4 shrink-0" /> {t("manualManagement.badge.markedNeedsRevision")}
        </p>
        {/* The mark REASON, shown inline (not just the badge hover-tooltip) so a flagged manual's problem is visible.
            Legacy stored it as JSON `{ text, section, step }` (migrated manuals carry that); the rewrite's own marks are
            plain text. {@link parseMarkComment} renders the legacy shape like the legacy report view, else the raw text. */}
        {comment
          ? (() => {
              const { text, where } = parseMarkComment(comment);
              return (
                <div className="border-destructive/40 bg-destructive/5 rounded-md border-l-2 px-3 py-2">
                  <p className="text-foreground text-sm break-words whitespace-pre-wrap">{text}</p>
                  {where ? <p className="text-muted-foreground mt-1 text-xs">{t("manualManagement.badge.reportedIn")} {where}</p> : null}
                </div>
              );
            })()
          : null}
      </div>
    );
  }
  const currentIndex = FLOW.findIndex((s) => s === state);
  const currentLabel = t(MANUAL_STATE_META[FLOW[currentIndex] ?? "draft"].labelKey);
  const accent = ACCENT[FLOW[currentIndex] ?? "draft"]; // the whole reached path glows the CURRENT state's colour
  return (
    <ol className="flex items-start" aria-label={t("manualManagement.badge.releaseStatus", { status: currentLabel })}>
      {FLOW.map((step, i) => {
        const Icon = MANUAL_STATE_META[step].Icon;
        const reached = i <= currentIndex;
        const isCurrent = i === currentIndex;
        // Every step is flex-1 (equal thirds), so the absolute connector — from THIS circle's centre spanning one column
        // — lands exactly on the NEXT circle's centre; the last step has none ⇒ no stray tail past Confirmed.
        return (
          <li key={step} className="relative flex flex-1 flex-col items-center gap-1">
            {i < FLOW.length - 1 ? (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute left-1/2 top-4 h-0.5 w-full -translate-y-1/2",
                  // Reached segments take the CURRENT state's accent (one colour up to the current dot); the rest is neutral.
                  i < currentIndex ? accent.line : "bg-border",
                )}
              />
            ) : null}
            <span
              className={cn(
                "bg-background relative z-10 flex size-8 items-center justify-center rounded-full",
                // Only the CURRENT step wears the state colour; earlier reached steps are neutral "done"; future = dashed.
                isCurrent ? accent.dot : reached ? "bg-muted text-muted-foreground" : "text-muted-foreground border border-dashed",
              )}
            >
              <Icon aria-hidden="true" className="size-4" />
            </span>
            <span className={cn("whitespace-nowrap text-[0.7rem]", isCurrent ? "text-foreground font-medium" : "text-muted-foreground")}>
              {t(MANUAL_STATE_META[step].labelKey)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
